import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Chip, Select, MenuItem, CircularProgress, Avatar,
} from '@mui/material';
import { tasksAPI, taskStatusesAPI } from '../../services/api';
import TaskEditDialog from '../TaskEditDialog';
import { getTaskType, taskTypeIcon } from '../../utils/taskTypes';
import CategoryChip from './CategoryChip';

const priorityColor = { high: 'error', medium: 'warning', low: 'default' };
const priorityLabel = { high: 'Hoch', medium: 'Mittel', low: 'Niedrig' };

/**
 * Flat list/table view of a project's tasks with inline status editing.
 * Reuses TaskEditDialog for full detail. Backend scopes all calls.
 */
function ProjectListView({ projectId }) {
  const [tasks, setTasks] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [tasksRes, statusRes] = await Promise.all([
        tasksAPI.getAll({ project_id: projectId }),
        taskStatusesAPI.getAll(projectId),
      ]);
      // Hide timetracker-only tasks from the PM list
      setTasks((tasksRes.data || []).filter((t) => !t.is_timetracker_only));
      setStatuses(statusRes.data || []);
    } catch (err) {
      console.error('[PM] list load error', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const statusById = (id) => statuses.find((s) => s.id === id);

  const handleStatusChange = async (task, newStatusId) => {
    try {
      await tasksAPI.update(task.id, { status_id: newStatusId });
      load();
    } catch (err) {
      console.error('[PM] status update error', err);
    }
  };

  const openTask = (task) => { setEditTask(task); setEditOpen(true); };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  }

  if (!tasks.length) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">Keine Tasks vorhanden</Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 600 }}>Task</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Zugewiesen</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Priorität</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Fällig</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Story Points</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((task) => {
              const st = statusById(task.status_id);
              return (
                <TableRow key={task.id} hover sx={{ cursor: 'pointer' }}>
                  <TableCell onClick={() => openTask(task)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box component="span" title={getTaskType(task.task_type).label}
                        sx={{ display: 'inline-flex', color: getTaskType(task.task_type).color }}>
                        {taskTypeIcon(task.task_type, { sx: { fontSize: 18 } })}
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{task.name}</Typography>
                        {task.task_id && (
                          <Typography variant="caption" color="text.secondary">{task.task_id}</Typography>
                        )}
                        {task.category_info && (
                          <Box sx={{ mt: 0.5 }}>
                            <CategoryChip category={task.category_info} sx={{ height: 18, fontSize: '0.65rem' }} />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={task.status_id || ''}
                      onChange={(e) => handleStatusChange(task, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ minWidth: 140 }}
                      renderValue={(val) => {
                        const s = statusById(val);
                        return (
                          <Chip
                            size="small"
                            label={s ? s.name : '—'}
                            sx={{ bgcolor: s?.color || '#999', color: '#fff' }}
                          />
                        );
                      }}
                    >
                      {statuses.map((s) => (
                        <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell onClick={() => openTask(task)}>
                    {task.assignee ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 26, height: 26, fontSize: '0.8rem', bgcolor: 'primary.main' }}>
                          {(task.assignee.full_name || task.assignee.username || '?').charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="body2">
                          {task.assignee.full_name || task.assignee.username}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell onClick={() => openTask(task)}>
                    <Chip size="small" color={priorityColor[task.priority] || 'default'}
                      label={priorityLabel[task.priority] || task.priority} variant="outlined" />
                  </TableCell>
                  <TableCell onClick={() => openTask(task)}>
                    <Typography variant="body2" color={st ? 'text.primary' : 'text.secondary'}>
                      {task.due_date ? new Date(task.due_date).toLocaleDateString('de-DE') : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" onClick={() => openTask(task)}>
                    <Typography variant="body2">{task.effort_estimate ?? '—'}</Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TaskEditDialog
        open={editOpen}
        onClose={() => { setEditOpen(false); setEditTask(null); }}
        task={editTask}
        projectId={projectId}
        onTaskUpdated={load}
      />
    </Box>
  );
}

export default ProjectListView;
